// apps/client/src/utils/businessTime.ts
//
// The client-side half of the business clock. The callables use
// services/client-functions/src/queries/shared/businessTimezone.ts; this file
// exists because apps/client cannot import from a service. The CONSTANT and the
// semantics must stay identical, or a page will ask for a window the server
// reads as a different window.
//
// Every tenant is an Australian broker group. Lead Attribution used to default
// its date pickers to the UTC date while the Monthly Report bucketed months in
// Brisbane, so for the ten hours a day the two clocks sit on different dates the
// page opened on the wrong window without saying so.

export const BUSINESS_TZ = 'Australia/Brisbane';

/** 'YYYY-MM-DD' for `now` in the business timezone. */
export function businessDate(now: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD, which is what the callables expect.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** 'YYYY-MM' for `now` in the business timezone. */
export function businessMonth(now: Date = new Date()): string {
  return businessDate(now).slice(0, 7);
}

/** 'YYYY-MM-01' for the business-timezone month `now` falls in. */
export function businessMonthStart(now: Date = new Date()): string {
  return `${businessMonth(now)}-01`;
}

/**
 * A Brisbane wall-clock date and time ('YYYY-MM-DD', 'HH:mm') as an ISO
 * instant. Brisbane observes no daylight saving, so the fixed +10:00 offset
 * is exact all year, and the result does not depend on the browser's own
 * timezone: a Melbourne user on AEDT who picks 00:30 means 00:30 in
 * Brisbane. Returns null for an unparseable input.
 */
export function brisbaneWallClockToIso(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const d = new Date(`${date}T${time}:00+10:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * True when `month` ('YYYY-MM') is the month currently in progress.
 *
 * A month still running is not comparable with a completed one: a
 * month-on-month fall measured against a part month is an artefact of the
 * calendar, not a change in performance.
 */
export function isMonthInProgress(month: string, now: Date = new Date()): boolean {
  return month === businessMonth(now);
}
